import { useRef, useState } from "react";
import { Upload, FileDown, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";

const ACTION = { create: ["New", "bg-emerald-50 text-emerald-800"], update: ["Update", "bg-sky-50 text-sky-800"], error: ["Fix needed", "bg-rose-50 text-rose-700"] };

async function downloadTemplate() {
  const r = await api.get("/customers/import/template", { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = "nexfresh_customers_template.xlsx";
  a.click();
}

function PreviewTable({ rows }) {
  return (
    <div className="max-h-[45vh] overflow-auto rounded-xl border border-[#E7E2D6]" data-testid="import-preview-table">
      <Table>
        <TableHeader><TableRow><TableHead>Row</TableHead><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Area</TableHead><TableHead className="text-right">Opening due</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.line} data-testid={`import-row-${r.line}`}>
              <TableCell className="font-mono text-xs">{r.line}</TableCell>
              <TableCell>{r.name}{r.existing_name && r.existing_name !== r.name && <div className="text-xs text-muted-foreground">was: {r.existing_name}</div>}</TableCell>
              <TableCell className="font-mono text-xs">{r.phone}</TableCell>
              <TableCell>{r.area}</TableCell>
              <TableCell className="text-right font-mono text-xs">{r.opening_balance ? inr(r.opening_balance) : "-"}</TableCell>
              <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION[r.action][1]}`}>{ACTION[r.action][0]}</span>{r.errors.length > 0 && <div className="mt-1 text-xs text-rose-700">{r.errors.join("; ")}</div>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export const CustomerImportDialog = ({ open, onOpenChange, onDone }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = (o) => { onOpenChange(o); if (!o) setPreview(null); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { setPreview((await api.post("/customers/import/preview", fd)).data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => r.action !== "error");
      const r = await api.post("/customers/import/commit", { rows });
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated${r.data.opening_total ? ` · ${inr(r.data.opening_total)} opening dues` : ""}`);
      close(false); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const s = preview?.summary;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Import customers from Excel</DialogTitle>
          <DialogDescription>Fill the template (Name and Phone are required). Existing phone numbers are updated, new ones are added. No WhatsApp is sent.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => downloadTemplate().catch((e) => toast.error(errMsg(e)))} data-testid="import-template-btn"><FileDown className="mr-2 h-4 w-4" />Download template</Button>
          <Button className="bg-[#0F382C] hover:bg-[#134E3A]" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="import-upload-btn">
            {busy && !preview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload .xlsx / .csv
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
        </div>
        {preview && <>
          <div className="flex flex-wrap gap-3 text-sm" data-testid="import-summary">
            <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-800">{s.create} new</span>
            <span className="rounded-full bg-sky-50 px-3 py-1 font-semibold text-sky-800">{s.update} to update</span>
            <span className="rounded-full bg-rose-50 px-3 py-1 font-semibold text-rose-700">{s.error} need fixing (skipped)</span>
          </div>
          <PreviewTable rows={preview.rows} />
        </>}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button disabled={!preview || busy || s.create + s.update === 0} className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={commit} data-testid="import-commit-btn">
            {busy && preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-2 h-4 w-4" />Import {preview ? s.create + s.update : ""} customers</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
